import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function TarifsPage() {
  const plans = [
    {
      name: 'Essentiel',
      price: '24.90',
      description: 'Parfait pour découvrir nos créations',
      features: [
        'Invitations animées personnalisées',
        'Format PDF haute résolution',
        'Livraison en 24h',
        '1 révision gratuite',
        'Support par email',
      ],
      color: 'from-blue-400 to-blue-600',
      popular: false,
    },
    {
      name: 'Premium',
      price: '34.90',
      description: 'Notre offre la plus populaire',
      features: [
        'Livres ou personnages personnalisés',
        'Formats PDF + JPG haute qualité',
        'Livraison express en 12h',
        '3 révisions gratuites',
        'Support prioritaire',
        'Aperçu avant finalisation',
      ],
      color: 'from-purple-400 to-pink-600',
      popular: true,
    },
    {
      name: 'Famille',
      price: '49.90',
      description: 'Idéal pour toute la famille',
      features: [
        'Portraits de famille illustrés',
        'Tous formats (PDF, JPG, PNG)',
        'Livraison ultra-rapide en 6h',
        'Révisions illimitées',
        'Support VIP 7j/7',
        'Aperçu en temps réel',
        'Fichiers sources inclus',
      ],
      color: 'from-yellow-400 to-orange-500',
      popular: false,
    },
  ];

  const addons = [
    {
      name: 'Livraison Express',
      price: '9.90',
      description: 'Recevez votre création en moins de 6 heures',
      icon: 'ri-flashlight-line',
    },
    {
      name: 'Révisions Supplémentaires',
      price: '4.90',
      description: 'Modifications illimitées après livraison',
      icon: 'ri-refresh-line',
    },
    {
      name: 'Formats Additionnels',
      price: '7.90',
      description: 'Recevez votre création dans tous les formats',
      icon: 'ri-file-list-line',
    },
    {
      name: 'Version Imprimée',
      price: '19.90',
      description: 'Recevez une version physique de qualité premium',
      icon: 'ri-printer-line',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-6xl font-fredoka font-bold text-gray-900 mb-6">
              Tarifs{' '}
              <span className="bg-gradient-to-r from-primary-blue via-purple-600 to-primary-coral bg-clip-text text-transparent">
                Transparents
              </span>
            </h1>
            <p className="text-xl font-quicksand text-gray-600 max-w-3xl mx-auto mb-4">
              Pas de frais cachés, pas d'abonnement. Payez uniquement pour ce que vous créez.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-mint/20 rounded-full">
              <i className="ri-shield-check-line text-primary-mint"></i>
              <span className="font-quicksand font-semibold text-gray-800">
                Garantie satisfait ou remboursé
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative ${
                  plan.popular ? 'md:scale-105 md:-mt-4' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <div className="px-6 py-2 bg-gradient-to-r from-primary-yellow to-orange-500 text-white font-quicksand font-bold rounded-full shadow-colored-yellow whitespace-nowrap">
                      Plus populaire
                    </div>
                  </div>
                )}
                <div
                  className={`bg-white rounded-4xl p-8 shadow-soft hover:shadow-soft-lg transition-all duration-300 h-full flex flex-col ${
                    plan.popular ? 'border-2 border-purple-600' : 'border border-gray-100'
                  }`}
                >
                  {/* Header */}
                  <div className="text-center mb-6">
                    <div className={`inline-flex px-4 py-2 bg-gradient-to-r ${plan.color} text-white font-quicksand font-bold rounded-full mb-4`}>
                      {plan.name}
                    </div>
                    <div className="mb-2">
                      <span className="text-5xl font-fredoka font-bold text-gray-900">
                        {plan.price}€
                      </span>
                    </div>
                    <p className="font-quicksand text-gray-500 text-sm">
                      par création
                    </p>
                    <p className="font-quicksand text-gray-600 mt-3">
                      {plan.description}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="flex-grow mb-8">
                    <ul className="space-y-4">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-mint/20 rounded-full mt-0.5">
                            <i className="ri-check-line text-primary-mint text-sm"></i>
                          </div>
                          <span className="font-quicksand text-gray-700">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <a
                    href="/creations"
                    className={`block w-full px-6 py-4 text-center font-quicksand font-bold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-colored-blue'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    Commencer
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-12"
          >
            <p className="font-quicksand text-gray-600">
              Tous les prix sont en euros TTC. Paiement sécurisé par Stripe.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-24 bg-neutral-lighter">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-fredoka font-bold text-gray-900 mb-4">
              Options Supplémentaires
            </h2>
            <p className="text-lg font-quicksand text-gray-600">
              Personnalisez votre expérience avec nos options à la carte
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {addons.map((addon, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-primary-blue to-purple-600 rounded-2xl mb-4">
                  <i className={`${addon.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-fredoka font-bold text-gray-900 mb-2">
                  {addon.name}
                </h3>
                <p className="font-quicksand text-gray-600 text-sm mb-4 leading-relaxed">
                  {addon.description}
                </p>
                <div className="text-2xl font-fredoka font-bold text-gray-900">
                  +{addon.price}€
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-primary-blue via-purple-600 to-primary-coral rounded-4xl p-12 text-center text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 right-10 w-48 h-48 bg-white rounded-full blur-3xl"></div>
            </div>
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-white/20 rounded-3xl backdrop-blur-sm">
                <i className="ri-building-line text-4xl text-white"></i>
              </div>
              <h2 className="text-4xl font-fredoka font-bold mb-4">
                Solutions Entreprise
              </h2>
              <p className="text-xl font-quicksand text-white/90 mb-8 max-w-2xl mx-auto">
                Besoin de créations en volume pour votre entreprise ? Nous proposons des tarifs dégressifs et un accompagnement personnalisé.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-quicksand font-bold rounded-full hover:shadow-soft-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                <span>Demander un devis</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-24 bg-neutral-lighter">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-4xl font-fredoka font-bold text-gray-900 mb-6">
              Des questions sur nos tarifs ?
            </h2>
            <p className="text-lg font-quicksand text-gray-600 mb-8">
              Consultez notre FAQ ou contactez-nous pour plus d'informations
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/faq"
                className="px-8 py-4 bg-gray-900 text-white font-quicksand font-bold rounded-full hover:shadow-soft-lg transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-question-line"></i>
                <span>Voir la FAQ</span>
              </a>
              <a
                href="/contact"
                className="px-8 py-4 bg-white border-2 border-gray-900 text-gray-900 font-quicksand font-bold rounded-full hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-customer-service-line"></i>
                <span>Nous contacter</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}